import { useSelector } from "react-redux";
import { Navigate, Outlet } from "react-router-dom";
import { Suspense } from "react";
import { lazyWithRetry as lazy } from "@src/utils/CommonUtils";
import { USER_CONSTANTS } from "@src/constants/UiConstants";
import { RootState } from "@src/app/store";
import CommonLoader from "@src/components/loader/CommonLoader";
import LazyWrapper from "@src/components/lazy-wrapper/LazyWrapper";

// Lazy-loaded page components — Question/Subject
const QuestionBaseComponent = lazy(
  () =>
    import("@src/pages/question/question-base-component/QuestionBaseComponent"),
);
const ProjectSetupPage = lazy(
  () => import("@src/pages/question/project-setup/ProjectSetupPage"),
);
const SubjectSpecificComponent = lazy(
  () =>
    import("@src/pages/subject/subject-specific-component/SubjectSpecificComponent"),
);
const SubjectLayoutComponent = lazy(
  () => import("@src/pages/subject/SubjectLayoutComponent"),
);
const SubjectSpecificLayout = lazy(
  () =>
    import("@src/pages/subject/subject-specific-component/SubjectSpecificLayout"),
);
const SubjectBaseComponent = lazy(
  () =>
    import("@src/pages/subject/subject-base-component/SubjectBaseComponent"),
);

// Lazy-loaded page components — Users
const SpecificUserComponent = lazy(
  () =>
    import("@src/pages/users/specific-user-component/SpecificUserComponent"),
);
const UserBaseComponent = lazy(
  () => import("@src/pages/users/user-base-component/UserBaseComponent"),
);
const SpecificStudentComponent = lazy(
  () =>
    import("@src/pages/users/specific-student-component/SpecificStudentComponent"),
);

// Lazy-loaded page components — Syllabus
const SyllabusBaseComponent = lazy(
  () =>
    import("@src/pages/syllabus/syllabus-base-compoenent/SyllabusBaseComponent"),
);
const CreateSyllabus = lazy(
  () => import("@src/pages/syllabus/create-syllabus/CreateSyllabus"),
);
const SyllabusSpecificComponent = lazy(
  () =>
    import("@src/pages/syllabus/syllabus-specific-component/SyllabusSpecificComponent"),
);
const TrainerSyllabusBaseComponent = lazy(
  () =>
    import("@src/pages/syllabus/trainer-syllabus/TrainerSyllabusBaseComponent"),
);

// Lazy-loaded page components — Quiz
const QuizBaseComponent = lazy(
  () => import("@src/pages/quiz/quiz-base-component/QuizBaseComponent"),
);
const SharedQuestions = lazy(
  () => import("@src/pages/question-verifier/shared-questions/SharedQuestions"),
);
const PracticeQuestionBaseComponent = lazy(
  () =>
    import("@src/pages/question/practice-question/PracticeQuestionBaseComponent"),
);
const QuizListTable = lazy(
  () => import("@src/pages/task-manager/quiz/quiz-table/QuizListTable"),
);
const QuizGroupListTable = lazy(
  () =>
    import("@src/pages/task-manager/quiz-group/quiz-group-table/QuizGroupListTable"),
);
const QuizGroupQuizzesList = lazy(
  () =>
    import("@src/pages/task-manager/quiz-group/quizzesList/QuizGroupQuizzesList"),
);
const QuizLayout = lazy(
  () => import("@src/pages/task-manager/quiz/quiz-table/QuizLayout"),
);

// Lazy-loaded page components — Student
const StudentProfile = lazy(
  () => import("@src/pages/student/student-profile/StudentProfile"),
);
const StudentLayoutComponent = lazy(
  () => import("@src/pages/student/StudentLayoutComponent"),
);
const StudentBatchList = lazy(
  () => import("@src/pages/batches/student-batch-list/StudentBatchList"),
);
const ResourceQuizList = lazy(
  () => import("@src/pages/student/student-dashboard/ResourceQuizList"),
);
const StudentElp = lazy(() => import("@src/pages/student/elp/StudentElp"));
const StudentAssignment = lazy(
  () => import("@src/pages/student/assignment/StudentAssignment"),
);
const StudentQuizLayout = lazy(
  () => import("@src/pages/student/StudentQuizLayout"),
);
const MyQuizList = lazy(
  () => import("@src/pages/student/my-quiz/my-quiz-list/MyQuizList"),
);
const StudentQuizGroupList = lazy(
  () =>
    import("@src/pages/student/quiz-group/quiz-group-list/StudentQuizGroupList"),
);
const MyQuizLayout = lazy(
  () => import("@src/pages/student/my-quiz/MyQuizLayout"),
);
const QuizGroupLayout = lazy(
  () => import("@src/pages/student/quiz-group/QuizGrouLayout"),
);
const StudentTournamentsList = lazy(
  () =>
    import("@src/pages/student/tournaments/student-tournaments-list/StudentTournamentsList"),
);
const ExamLayoutScreen = lazy(
  () => import("@src/pages/student/exam-layout/ExamLayoutScreen"),
);
const InstructionLayout = lazy(
  () => import("@src/pages/student/instructions/InstructionLayout"),
);
const ResultDetails = lazy(
  () => import("@src/pages/student/student-evaluation/ResultDetails"),
);
const RenderQuizGroup = lazy(
  () =>
    import("@src/pages/student/quiz-group/render-quiz-group/RenderQuizGroup"),
);
const StudentFeedbackPage = lazy(
  () => import("@src/pages/student/feedback/StudentFeedbackPage"),
);

// Lazy-loaded page components — Organization
const OrganizationLayout = lazy(
  () => import("@src/pages/super-admin/organization/OrganizationLayout"),
);
const OrganizationListTable = lazy(
  () =>
    import("@src/pages/super-admin/organization/organization-table-component/OrganizationListTable"),
);
const SubOrganizationListTable = lazy(
  () =>
    import("@src/pages/super-admin/sub-organization/sub-organization-table/SubOrganizationListTable"),
);
const OrganizationUsersList = lazy(
  () =>
    import("@src/pages/super-admin/organization/org-users/OrganizationUsersList"),
);
const OrgBranchTable = lazy(
  () => import("@src/pages/super-admin/org-branch-table/OrgBranchTable"),
);
const OrgBatchTable = lazy(
  () => import("@src/pages/super-admin/org-batch-table/OrgBatchTable"),
);
const OrgBatchLayout = lazy(
  () => import("@src/pages/super-admin/org-batch-table/OrgBatchLayout"),
);
const CreateBatch = lazy(() => import("@src/pages/creation-forms/CreateBatch"));

// Lazy-loaded page components — Batch/Reports
const BatchBaseComponent = lazy(
  () => import("@src/pages/batches/batch-base-component/BatchBaseComponent"),
);
const LandingPage = lazy(
  () => import("@src/pages/batches/attendance/landing-page/LandingPage"),
);
const StudentsElpReportTable = lazy(
  () => import("@src/pages/batches/student-elp-report/StudentsElpReportTable"),
);
const StudentElpTree = lazy(
  () => import("@src/pages/batches/student-elp-tree/StudentElpTree"),
);
const ElpReportQuestionDetails = lazy(
  () =>
    import("@src/pages/batches/elp-report-question-details/ElpReportQuestionDeatils"),
);
const CourseDashboardLayout = lazy(
  () => import("@src/pages/batches/course-dashboard/CourseDashboardLayout"),
);
const CourseDashboard = lazy(
  () => import("@src/pages/batches/course-dashboard/CourseDashboard"),
);

// Lazy-loaded page components — Super Admin
const RegistrationApprovalComponent = lazy(
  () =>
    import("@src/pages/super-admin/registration-approval/RegistrationApprovalComponent"),
);
const ApprovalLayout = lazy(
  () => import("@src/pages/super-admin/registration-approval/ApprovalLayout"),
);
const CategoryTemplateList = lazy(
  () =>
    import("@src/pages/super-admin/org-registration/category-template/CategoryTemplateList"),
);
const OrgRegistrationForm = lazy(
  () =>
    import("@src/pages/super-admin/org-registration/org-registration-form/OrgRegistrationForm"),
);
const TemplateLayout = lazy(
  () => import("@src/pages/super-admin/org-registration/TemplateLayout"),
);
const TutorialBaseComponent = lazy(
  () => import("@src/pages/super-admin/tutorials/TutorialBaseComponent"),
);
const PlaygroundBaseComponent = lazy(
  () => import("@src/pages/super-admin/playground/PlaygroundBaseComponent"),
);
const TraitsLayout = lazy(
  () => import("@src/pages/super-admin/traits/TraitsLayout"),
);
const TraitsList = lazy(
  () => import("@src/pages/super-admin/traits/TraitsList"),
);
const ParagraphsLayout = lazy(
  () => import("@src/pages/super-admin/paragraphs/ParagraphsLayout"),
);
const ParagraphsList = lazy(
  () => import("@src/pages/super-admin/paragraphs/ParagraphsList"),
);
const DashboardLayout = lazy(
  () => import("@src/pages/super-admin/dashboard/DashboardLayout"),
);
const ObjectivesBaseComponent = lazy(
  () => import("@src/pages/super-admin/objectives/ObjectivesBaseComponent"),
);
const DatabaseManagementPage = lazy(
  () => import("@src/pages/database-management/DatabaseManagementPage"),
);

// Lazy-loaded page components — Super Task Manager
const CategoriesLayout = lazy(
  () => import("@src/pages/super_task_manager/categories/CategoriesLayout"),
);
const CategoriesTable = lazy(
  () =>
    import("@src/pages/super_task_manager/categories/categories-table/CategoriesTable"),
);
const SuperOrganizationListTable = lazy(
  () =>
    import("@src/pages/super_task_manager/organizations-table/SuperOrganizationListTable"),
);
const CompaniesListTable = lazy(
  () => import("@src/pages/super_task_manager/companies/CompaniesListTable"),
);
const CategoryQuestionTable = lazy(
  () => import("@src/pages/super_task_manager/questions/CategoryQuestionTable"),
);
const CategoryTopicsTable = lazy(
  () => import("@src/pages/super_task_manager/topics/CategoryTopicsTable"),
);
const CategoryListTable = lazy(
  () => import("@src/pages/super_task_manager/lists/CategoriesListTable"),
);

// Lazy-loaded page components — Tournaments
const TournamentLayout = lazy(
  () => import("@src/pages/task-manager/tournaments/TournamentLayout"),
);
const TournamentsListTable = lazy(
  () =>
    import("@src/pages/task-manager/tournaments/tournaments-table/TournamentsListTable"),
);
const QuestionDetails = lazy(
  () =>
    import("@src/pages/task-manager/tournaments/completed-tournament-questions/QuestionDetails"),
);
const CompletedTournamentDetailsList = lazy(
  () =>
    import("@src/pages/task-manager/tournaments/completed-tournament-questions/CompletedTournamentDetailsList"),
);

// Lazy-loaded page components — Dashboard/Misc
const DashboardBaseComponent = lazy(
  () =>
    import("@src/pages/dashboard/dashboard-base-component/DashboardBaseComponent"),
);
const IndividualStudentQuizReportLayout = lazy(
  () =>
    import("@src/pages/task-manager/quiz/quiz-table/psychometric-quiz-report/individual-student-quiz-report/layout/IndividualStudentQuizReportLayout"),
);
const ClientTable = lazy(() => import("@src/pages/clients/tables/ClientTable"));
const ClientDashboard = lazy(() =>
  import("@src/pages/clients/client-dashboard/ClientDashboard").then((m) => ({
    default: m.ClientDashboard,
  })),
);
const ClientLayout = lazy(
  () => import("@src/pages/clients/client-layout/ClientLayout"),
);
const Workspace = lazy(() => import("@src/components/workspace/Workspace"));
const TrainerElpComponent = lazy(
  () => import("@src/pages/trainer/elp/TrainerElpComponent"),
);
const WordzappLayout = lazy(() => import("@src/pages/wordzapp/WordzappLayout"));
const WordzappHome = lazy(() => import("@src/pages/wordzapp/WordzappHome"));
const WordzappListsView = lazy(
  () => import("@src/pages/wordzapp/lists/WordzappListsView"),
);

const SubjectSpecificComponentWithRole = () => {
  const userRole = useSelector(
    (state: RootState) => state.globalSlice.userDetails?.currentPrivilege,
  );
  if (
    userRole === USER_CONSTANTS.QUESTION_ADDER ||
    userRole === USER_CONSTANTS.QUESTION_VERIFIER
  ) {
    return (
      <LazyWrapper>
        <QuestionBaseComponent />
      </LazyWrapper>
    );
  } else {
    return (
      <LazyWrapper>
        <SubjectSpecificComponent />
      </LazyWrapper>
    );
  }
};
const SubjectBaseComponentWithRole = () => {
  const userRole = useSelector(
    (state: RootState) => state.globalSlice.userDetails?.currentPrivilege,
  );
  if (userRole === USER_CONSTANTS.TRAINER) {
    return (
      <LazyWrapper>
        <TrainerSyllabusBaseComponent />
      </LazyWrapper>
    );
  } else {
    return (
      <LazyWrapper>
        <SubjectBaseComponent />
      </LazyWrapper>
    );
  }
};
const UserBaseComponentWithRole = () => {
  const userRole = useSelector(
    (state: RootState) => state.globalSlice.userDetails?.currentPrivilege,
  );
  if (
    userRole === USER_CONSTANTS.SUPER_USER ||
    userRole === USER_CONSTANTS.SUPER_ADMIN
  ) {
    return (
      <LazyWrapper>
        <UserBaseComponent />
      </LazyWrapper>
    );
  } else {
    return (
      <LazyWrapper>
        <SpecificUserComponent />
      </LazyWrapper>
    );
  }
};
const OrganizationComponentWithRole = () => {
  const userRole = useSelector(
    (state: RootState) => state.globalSlice.userDetails?.currentPrivilege,
  );
  if (userRole === USER_CONSTANTS.SUPER_TASK_MANAGER) {
    return (
      <LazyWrapper>
        <SuperOrganizationListTable />
      </LazyWrapper>
    );
  } else {
    return (
      <LazyWrapper>
        <OrganizationListTable />
      </LazyWrapper>
    );
  }
};
export const NavbarRoutes = [
  {
    path: "batch/course-dashboard",
    element: (
      <LazyWrapper>
        <CourseDashboardLayout />
      </LazyWrapper>
    ),
    children: [
      {
        path: "",
        element: (
          <LazyWrapper>
            <CourseDashboard />
          </LazyWrapper>
        ),
      },
      {
        path: "details",
        children: [
          {
            path: "",
            element: (
              <LazyWrapper>
                <StudentElpTree pageTreetype="syllabus" />
              </LazyWrapper>
            ),
          },
          {
            path: "question",
            element: (
              <LazyWrapper>
                <ElpReportQuestionDetails />
              </LazyWrapper>
            ),
          },
        ],
      },
    ],
  },
  {
    path: "",
    element: <Navigate to="login" />,
  },
  {
    path: "/subjects",
    element: (
      <LazyWrapper>
        <SubjectLayoutComponent />
      </LazyWrapper>
    ),
    children: [
      {
        path: "",
        element: <SubjectBaseComponentWithRole />,
      },
      {
        path: "organizations",
        element: (
          <LazyWrapper>
            <OrganizationListTable />
          </LazyWrapper>
        ),
      },
    ],
  },
  {
    path: "registration-approval",
    element: (
      <LazyWrapper>
        <ApprovalLayout />
      </LazyWrapper>
    ),
    children: [
      {
        path: "",
        element: (
          <LazyWrapper>
            <RegistrationApprovalComponent />
          </LazyWrapper>
        ),
      },
      {
        path: "form",
        element: (
          <LazyWrapper>
            <OrgRegistrationForm />
          </LazyWrapper>
        ),
      },
    ],
  },
  {
    path: "student-profile",
    element: (
      <LazyWrapper>
        <StudentProfile />
      </LazyWrapper>
    ),
  },
  {
    path: "/users",
    element: <UserBaseComponentWithRole />,
  },
  {
    path: "/organization",
    element: (
      <LazyWrapper>
        <SubOrganizationListTable />
      </LazyWrapper>
    ),
  },
  {
    path: "/organization/sub-organization",
    element: (
      <LazyWrapper>
        <SubOrganizationListTable />
      </LazyWrapper>
    ),
  },
  {
    path: "/organization/branch",
    element: (
      <LazyWrapper>
        <OrgBranchTable />
      </LazyWrapper>
    ),
  },
  {
    path: "/organization/batch/create-batch",
    element: (
      <LazyWrapper>
        <CreateBatch />
      </LazyWrapper>
    ),
  },
  {
    path: "/organization/batch",
    element: (
      <LazyWrapper>
        <OrgBatchTable />
      </LazyWrapper>
    ),
  },
  {
    path: "/organization/users",
    element: (
      <LazyWrapper>
        <OrganizationUsersList />
      </LazyWrapper>
    ),
  },
  {
    path: "/organization/clients",
    element: (
      <Suspense fallback={<CommonLoader color="var(--primary-color)" />}>
        <ClientTable />
      </Suspense>
    ),
  },
  {
    path: "/organization/clients/client-dashboard",
    element: (
      <LazyWrapper>
        <ClientDashboard />
      </LazyWrapper>
    ),
  },
  {
    path: "organizations",
    element: (
      <LazyWrapper>
        <OrganizationLayout />
      </LazyWrapper>
    ),
    children: [
      { path: "", element: <OrganizationComponentWithRole /> },
      {
        path: "sub-organization",
        element: (
          <LazyWrapper>
            <SubOrganizationListTable />
          </LazyWrapper>
        ),
      },
      {
        path: "branch",
        element: (
          <LazyWrapper>
            <ClientLayout />
          </LazyWrapper>
        ),
        children: [
          {
            path: "",
            element: (
              <Suspense
                fallback={<CommonLoader color="var(--primary-color)" />}
              >
                <OrgBranchTable />
              </Suspense>
            ),
          },
          {
            path: "branch-dashboard",
            element: <Outlet />,
            children: [
              {
                path: "",
                element: (
                  <LazyWrapper>
                    <ClientDashboard />
                  </LazyWrapper>
                ),
              },
              {
                path: "batch-dashboard",
                children: [
                  {
                    path: "",
                    element: (
                      <LazyWrapper>
                        <LandingPage />
                      </LazyWrapper>
                    ),
                  },
                  {
                    path: "details",
                    children: [
                      {
                        path: "",
                        element: (
                          <LazyWrapper>
                            <StudentElpTree pageTreetype="syllabus" />
                          </LazyWrapper>
                        ),
                      },
                      {
                        path: "question",
                        element: (
                          <LazyWrapper>
                            <ElpReportQuestionDetails />
                          </LazyWrapper>
                        ),
                      },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        path: "quiz",
        element: (
          <LazyWrapper>
            <QuizLayout />
          </LazyWrapper>
        ),
        children: [
          {
            path: "",
            element: (
              <LazyWrapper>
                <QuizListTable />
              </LazyWrapper>
            ),
          },
          {
            path: "student-quiz-details",
            element: (
              <LazyWrapper>
                <IndividualStudentQuizReportLayout />
              </LazyWrapper>
            ),
          },
        ],
      },
      {
        path: "sub-organization/quiz",
        element: (
          <LazyWrapper>
            <QuizLayout />
          </LazyWrapper>
        ),
        children: [
          {
            path: "",
            element: (
              <LazyWrapper>
                <QuizListTable />
              </LazyWrapper>
            ),
          },
          {
            path: "student-quiz-details",
            element: (
              <LazyWrapper>
                <IndividualStudentQuizReportLayout />
              </LazyWrapper>
            ),
          },
        ],
      },
      {
        path: "clients/quiz",
        element: (
          <LazyWrapper>
            <QuizLayout />
          </LazyWrapper>
        ),
        children: [
          {
            path: "",
            element: (
              <LazyWrapper>
                <QuizListTable />
              </LazyWrapper>
            ),
          },
          {
            path: "student-quiz-details",
            element: (
              <LazyWrapper>
                <IndividualStudentQuizReportLayout />
              </LazyWrapper>
            ),
          },
        ],
      },
      {
        path: "branch/quiz",
        element: (
          <LazyWrapper>
            <QuizLayout />
          </LazyWrapper>
        ),
        children: [
          {
            path: "",
            element: (
              <LazyWrapper>
                <QuizListTable />
              </LazyWrapper>
            ),
          },
          {
            path: "student-quiz-details",
            element: (
              <LazyWrapper>
                <IndividualStudentQuizReportLayout />
              </LazyWrapper>
            ),
          },
        ],
      },
      {
        path: "clients/branch/quiz",
        element: (
          <LazyWrapper>
            <QuizLayout />
          </LazyWrapper>
        ),
        children: [
          {
            path: "",
            element: (
              <LazyWrapper>
                <QuizListTable />
              </LazyWrapper>
            ),
          },
          {
            path: "student-quiz-details",
            element: (
              <LazyWrapper>
                <IndividualStudentQuizReportLayout />
              </LazyWrapper>
            ),
          },
        ],
      },
      {
        path: "sub-organization/clients/quiz",
        element: (
          <LazyWrapper>
            <QuizLayout />
          </LazyWrapper>
        ),
        children: [
          {
            path: "",
            element: (
              <LazyWrapper>
                <QuizListTable />
              </LazyWrapper>
            ),
          },
          {
            path: "student-quiz-details",
            element: (
              <LazyWrapper>
                <IndividualStudentQuizReportLayout />
              </LazyWrapper>
            ),
          },
        ],
      },
      {
        path: "sub-organization/branch/quiz",
        element: (
          <LazyWrapper>
            <QuizLayout />
          </LazyWrapper>
        ),
        children: [
          {
            path: "",
            element: (
              <LazyWrapper>
                <QuizListTable />
              </LazyWrapper>
            ),
          },
          {
            path: "student-quiz-details",
            element: (
              <LazyWrapper>
                <IndividualStudentQuizReportLayout />
              </LazyWrapper>
            ),
          },
        ],
      },
      {
        path: "sub-organization/clients/branch/quiz",
        element: (
          <LazyWrapper>
            <QuizLayout />
          </LazyWrapper>
        ),
        children: [
          {
            path: "",
            element: (
              <LazyWrapper>
                <QuizListTable />
              </LazyWrapper>
            ),
          },
          {
            path: "student-quiz-details",
            element: (
              <LazyWrapper>
                <IndividualStudentQuizReportLayout />
              </LazyWrapper>
            ),
          },
        ],
      },
      {
        path: "quiz-group",
        element: (
          <LazyWrapper>
            <QuizGroupListTable />
          </LazyWrapper>
        ),
      },
      {
        path: "quizGroup",
        element: (
          <LazyWrapper>
            <QuizGroupListTable />
          </LazyWrapper>
        ),
      },
      {
        path: "tournaments",
        element: (
          <Suspense fallback="Loading...">
            <TournamentsListTable />
          </Suspense>
        ),
      },
      {
        path: "sub-organization/tournaments",
        element: (
          <Suspense fallback="Loading...">
            <TournamentsListTable />
          </Suspense>
        ),
      },
      {
        path: "sub-organization/quiz-group",
        element: (
          <LazyWrapper>
            <QuizGroupListTable />
          </LazyWrapper>
        ),
      },
      {
        path: "sub-organization/branch/quiz-group",
        element: (
          <LazyWrapper>
            <QuizGroupListTable />
          </LazyWrapper>
        ),
      },
      {
        path: "sub-organization/clients/tournaments",
        element: (
          <Suspense fallback="Loading...">
            <TournamentsListTable />
          </Suspense>
        ),
      },
      {
        path: "sub-organization/clients/quiz-group",
        element: (
          <LazyWrapper>
            <QuizGroupListTable />
          </LazyWrapper>
        ),
      },
      {
        path: "sub-organization/branch/tournaments",
        element: (
          <Suspense fallback="Loading...">
            <TournamentsListTable />
          </Suspense>
        ),
      },
      {
        path: "sub-organization/clients/branch/tournaments",
        element: (
          <Suspense fallback="Loading...">
            <TournamentsListTable />
          </Suspense>
        ),
      },
      {
        path: "sub-organization/clients/branch/quiz-group",
        element: (
          <LazyWrapper>
            <QuizGroupListTable />
          </LazyWrapper>
        ),
      },
      {
        path: "clients/branch/tournaments",
        element: (
          <Suspense fallback="Loading...">
            <TournamentsListTable />
          </Suspense>
        ),
      },
      {
        path: "clients/branch/quiz-group",
        element: (
          <LazyWrapper>
            <QuizGroupListTable />
          </LazyWrapper>
        ),
      },
      {
        path: "branch/tournaments",
        element: (
          <Suspense fallback="Loading...">
            <TournamentsListTable />
          </Suspense>
        ),
      },
      {
        path: "branch/quiz-group",
        element: (
          <LazyWrapper>
            <QuizGroupListTable />
          </LazyWrapper>
        ),
      },
      {
        path: "clients/tournaments",
        element: (
          <Suspense fallback="Loading...">
            <TournamentsListTable />
          </Suspense>
        ),
      },
      {
        path: "clients/quiz-group",
        element: (
          <LazyWrapper>
            <QuizGroupListTable />
          </LazyWrapper>
        ),
      },
      {
        path: "",
        element: (
          <LazyWrapper>
            <OrgBatchLayout />
          </LazyWrapper>
        ),
        children: [
          {
            path: "",
            element: (
              <LazyWrapper>
                <OrgBatchTable />
              </LazyWrapper>
            ),
          },
          {
            path: "create-batch",
            element: (
              <LazyWrapper>
                <CreateBatch />
              </LazyWrapper>
            ),
          },
        ],
      },
      {
        path: "branch",
        element: (
          <LazyWrapper>
            <OrgBranchTable />
          </LazyWrapper>
        ),
      },
      {
        path: "batch",
        element: (
          <LazyWrapper>
            <OrgBatchLayout />
          </LazyWrapper>
        ),
        children: [
          {
            path: "",
            element: (
              <LazyWrapper>
                <OrgBatchTable />
              </LazyWrapper>
            ),
          },
          {
            path: "create-batch",
            element: (
              <LazyWrapper>
                <CreateBatch />
              </LazyWrapper>
            ),
          },
          {
            path: "batch-dashboard",
            children: [
              {
                path: "",
                element: (
                  <LazyWrapper>
                    <LandingPage />
                  </LazyWrapper>
                ),
              },
              {
                path: "details",
                children: [
                  {
                    path: "",
                    element: (
                      <LazyWrapper>
                        <StudentElpTree pageTreetype="syllabus" />
                      </LazyWrapper>
                    ),
                  },
                  {
                    path: "question",
                    element: (
                      <LazyWrapper>
                        <ElpReportQuestionDetails />
                      </LazyWrapper>
                    ),
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        path: "users",
        element: (
          <LazyWrapper>
            <OrganizationUsersList />
          </LazyWrapper>
        ),
      },
      {
        path: "registration-approval",
        element: (
          <LazyWrapper>
            <RegistrationApprovalComponent />
          </LazyWrapper>
        ),
      },
      {
        path: "sub-organization/branch",
        element: (
          <LazyWrapper>
            <ClientLayout />
          </LazyWrapper>
        ),
        children: [
          {
            path: "",
            element: (
              <Suspense
                fallback={<CommonLoader color="var(--primary-color)" />}
              >
                <OrgBranchTable />
              </Suspense>
            ),
          },
          {
            path: "branch-dashboard",
            element: <Outlet />,
            children: [
              {
                path: "",
                element: (
                  <LazyWrapper>
                    <ClientDashboard />
                  </LazyWrapper>
                ),
              },
              {
                path: "batch-dashboard",
                children: [
                  {
                    path: "",
                    element: (
                      <LazyWrapper>
                        <LandingPage />
                      </LazyWrapper>
                    ),
                  },
                  {
                    path: "details",
                    children: [
                      {
                        path: "",
                        element: (
                          <LazyWrapper>
                            <StudentElpTree pageTreetype="syllabus" />
                          </LazyWrapper>
                        ),
                      },
                      {
                        path: "question",
                        element: (
                          <LazyWrapper>
                            <ElpReportQuestionDetails />
                          </LazyWrapper>
                        ),
                      },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        path: "sub-organization/branch",
        element: (
          <LazyWrapper>
            <OrgBranchTable />
          </LazyWrapper>
        ),
      },
      {
        path: "clients/branch",
        element: (
          <LazyWrapper>
            <OrgBranchTable />
          </LazyWrapper>
        ),
      },
      {
        path: "sub-organization/clients/branch",
        element: (
          <LazyWrapper>
            <OrgBranchTable />
          </LazyWrapper>
        ),
      },
      {
        path: "sub-organization/branch/users",
        element: (
          <LazyWrapper>
            <OrganizationUsersList />
          </LazyWrapper>
        ),
      },
      {
        path: "sub-organization/clients/branch/users",
        element: (
          <LazyWrapper>
            <OrganizationUsersList />
          </LazyWrapper>
        ),
      },
      {
        path: "clients/branch/users",
        element: (
          <LazyWrapper>
            <OrganizationUsersList />
          </LazyWrapper>
        ),
      },
      {
        path: "branch/users",
        element: (
          <LazyWrapper>
            <OrganizationUsersList />
          </LazyWrapper>
        ),
      },
      {
        path: "sub-organization/clients/batch",
        element: (
          <LazyWrapper>
            <OrgBatchLayout />
          </LazyWrapper>
        ),
        children: [
          {
            path: "",
            element: (
              <LazyWrapper>
                <OrgBatchTable />
              </LazyWrapper>
            ),
          },
          {
            path: "create-batch",
            element: (
              <LazyWrapper>
                <CreateBatch />
              </LazyWrapper>
            ),
          },
          {
            path: "batch-dashboard",
            children: [
              {
                path: "",
                element: (
                  <LazyWrapper>
                    <LandingPage />
                  </LazyWrapper>
                ),
              },
              {
                path: "details",
                children: [
                  {
                    path: "",
                    element: (
                      <LazyWrapper>
                        <StudentElpTree pageTreetype="syllabus" />
                      </LazyWrapper>
                    ),
                  },
                  {
                    path: "question",
                    element: (
                      <LazyWrapper>
                        <ElpReportQuestionDetails />
                      </LazyWrapper>
                    ),
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        path: "clients/branch/batch",
        element: (
          <LazyWrapper>
            <OrgBatchLayout />
          </LazyWrapper>
        ),
        children: [
          {
            path: "",
            element: (
              <LazyWrapper>
                <OrgBatchTable />
              </LazyWrapper>
            ),
          },
          {
            path: "create-batch",
            element: (
              <LazyWrapper>
                <CreateBatch />
              </LazyWrapper>
            ),
          },
          {
            path: "batch-dashboard",
            children: [
              {
                path: "",
                element: (
                  <LazyWrapper>
                    <LandingPage />
                  </LazyWrapper>
                ),
              },
              {
                path: "details",
                children: [
                  {
                    path: "",
                    element: (
                      <LazyWrapper>
                        <StudentElpTree pageTreetype="syllabus" />
                      </LazyWrapper>
                    ),
                  },
                  {
                    path: "question",
                    element: (
                      <LazyWrapper>
                        <ElpReportQuestionDetails />
                      </LazyWrapper>
                    ),
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        path: "clients/batch",
        element: (
          <LazyWrapper>
            <OrgBatchLayout />
          </LazyWrapper>
        ),
        children: [
          {
            path: "",
            element: (
              <LazyWrapper>
                <OrgBatchTable />
              </LazyWrapper>
            ),
          },
          {
            path: "create-batch",
            element: (
              <LazyWrapper>
                <CreateBatch />
              </LazyWrapper>
            ),
          },
          {
            path: "batch-dashboard",
            children: [
              {
                path: "",
                element: (
                  <LazyWrapper>
                    <LandingPage />
                  </LazyWrapper>
                ),
              },
              {
                path: "details",
                children: [
                  {
                    path: "",
                    element: (
                      <LazyWrapper>
                        <StudentElpTree pageTreetype="syllabus" />
                      </LazyWrapper>
                    ),
                  },
                  {
                    path: "question",
                    element: (
                      <LazyWrapper>
                        <ElpReportQuestionDetails />
                      </LazyWrapper>
                    ),
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        path: "branch/batch",
        element: (
          <LazyWrapper>
            <OrgBatchLayout />
          </LazyWrapper>
        ),
        children: [
          {
            path: "",
            element: (
              <LazyWrapper>
                <OrgBatchTable />
              </LazyWrapper>
            ),
          },
          {
            path: "create-batch",
            element: (
              <LazyWrapper>
                <CreateBatch />
              </LazyWrapper>
            ),
          },
          {
            path: "batch-dashboard",
            children: [
              {
                path: "",
                element: (
                  <LazyWrapper>
                    <LandingPage />
                  </LazyWrapper>
                ),
              },
              {
                path: "details",
                children: [
                  {
                    path: "",
                    element: (
                      <LazyWrapper>
                        <StudentElpTree pageTreetype="syllabus" />
                      </LazyWrapper>
                    ),
                  },
                  {
                    path: "question",
                    element: (
                      <LazyWrapper>
                        <ElpReportQuestionDetails />
                      </LazyWrapper>
                    ),
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        path: "sub-organization/clients/branch/batch",
        element: (
          <LazyWrapper>
            <OrgBatchLayout />
          </LazyWrapper>
        ),
        children: [
          {
            path: "",
            element: (
              <LazyWrapper>
                <OrgBatchTable />
              </LazyWrapper>
            ),
          },
          {
            path: "create-batch",
            element: (
              <LazyWrapper>
                <CreateBatch />
              </LazyWrapper>
            ),
          },
          {
            path: "batch-dashboard",
            children: [
              {
                path: "",
                element: (
                  <LazyWrapper>
                    <LandingPage />
                  </LazyWrapper>
                ),
              },
              {
                path: "details",
                children: [
                  {
                    path: "",
                    element: (
                      <LazyWrapper>
                        <StudentElpTree pageTreetype="syllabus" />
                      </LazyWrapper>
                    ),
                  },
                  {
                    path: "question",
                    element: (
                      <LazyWrapper>
                        <ElpReportQuestionDetails />
                      </LazyWrapper>
                    ),
                  },
                ],
              },
            ],
          },
        ],
      },

      {
        path: "sub-organization/batch",
        element: (
          <LazyWrapper>
            <OrgBatchLayout />
          </LazyWrapper>
        ),
        children: [
          {
            path: "",
            element: (
              <LazyWrapper>
                <OrgBatchTable />
              </LazyWrapper>
            ),
          },
          {
            path: "create-batch",
            element: (
              <LazyWrapper>
                <CreateBatch />
              </LazyWrapper>
            ),
          },
          {
            path: "batch-dashboard",
            children: [
              {
                path: "",
                element: (
                  <LazyWrapper>
                    <LandingPage />
                  </LazyWrapper>
                ),
              },
              {
                path: "details",
                children: [
                  {
                    path: "",
                    element: (
                      <LazyWrapper>
                        <StudentElpTree pageTreetype="syllabus" />
                      </LazyWrapper>
                    ),
                  },
                  {
                    path: "question",
                    element: (
                      <LazyWrapper>
                        <ElpReportQuestionDetails />
                      </LazyWrapper>
                    ),
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        path: "sub-organization/branch/batch",
        element: (
          <LazyWrapper>
            <OrgBatchLayout />
          </LazyWrapper>
        ),
        children: [
          {
            path: "",
            element: (
              <LazyWrapper>
                <OrgBatchTable />
              </LazyWrapper>
            ),
          },
          {
            path: "create-batch",
            element: (
              <LazyWrapper>
                <CreateBatch />
              </LazyWrapper>
            ),
          },
          {
            path: "batch-dashboard",
            children: [
              {
                path: "",
                element: (
                  <LazyWrapper>
                    <LandingPage />
                  </LazyWrapper>
                ),
              },
              {
                path: "details",
                children: [
                  {
                    path: "",
                    element: (
                      <LazyWrapper>
                        <StudentElpTree pageTreetype="syllabus" />
                      </LazyWrapper>
                    ),
                  },
                  {
                    path: "question",
                    element: (
                      <LazyWrapper>
                        <ElpReportQuestionDetails />
                      </LazyWrapper>
                    ),
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        path: "sub-organization/users",
        element: (
          <LazyWrapper>
            <OrganizationUsersList />
          </LazyWrapper>
        ),
      },
      {
        path: "clients/users",
        element: (
          <LazyWrapper>
            <OrganizationUsersList />
          </LazyWrapper>
        ),
      },
      {
        path: "sub-organization/registration-approval",
        element: (
          <LazyWrapper>
            <RegistrationApprovalComponent />
          </LazyWrapper>
        ),
      },
      {
        path: "branch/registration-approval",
        element: (
          <LazyWrapper>
            <RegistrationApprovalComponent />
          </LazyWrapper>
        ),
      },
      {
        path: "sub-organization/branch/registration-approval",
        element: (
          <LazyWrapper>
            <RegistrationApprovalComponent />
          </LazyWrapper>
        ),
      },
      {
        path: "batch/students-report",
        element: (
          <>
            <Outlet />
          </>
        ),
        children: [
          {
            path: "",
            element: (
              <LazyWrapper>
                <StudentsElpReportTable />
              </LazyWrapper>
            ),
          },
          {
            path: "details",
            children: [
              {
                path: "",
                element: (
                  <LazyWrapper>
                    <StudentElpTree pageTreetype="syllabus" />
                  </LazyWrapper>
                ),
              },
              {
                path: "question",
                element: (
                  <LazyWrapper>
                    <ElpReportQuestionDetails />
                  </LazyWrapper>
                ),
              },
            ],
          },
        ],
      },
      {
        path: "sub-organization/clients/batch/students-report",
        element: (
          <>
            <Outlet />
          </>
        ),
        children: [
          {
            path: "",
            element: (
              <LazyWrapper>
                <StudentsElpReportTable />
              </LazyWrapper>
            ),
          },
          {
            path: "details",
            children: [
              {
                path: "",
                element: (
                  <LazyWrapper>
                    <StudentElpTree pageTreetype="syllabus" />
                  </LazyWrapper>
                ),
              },
              {
                path: "question",
                element: (
                  <LazyWrapper>
                    <ElpReportQuestionDetails />
                  </LazyWrapper>
                ),
              },
            ],
          },
        ],
      },
      {
        path: "clients/batch/students-report",
        element: (
          <>
            <Outlet />
          </>
        ),
        children: [
          {
            path: "",
            element: (
              <LazyWrapper>
                <StudentsElpReportTable />
              </LazyWrapper>
            ),
          },
          {
            path: "details",
            children: [
              {
                path: "",
                element: (
                  <LazyWrapper>
                    <StudentElpTree pageTreetype="syllabus" />
                  </LazyWrapper>
                ),
              },
              {
                path: "question",
                element: (
                  <LazyWrapper>
                    <ElpReportQuestionDetails />
                  </LazyWrapper>
                ),
              },
            ],
          },
        ],
      },
      {
        path: "sub-organization/clients/branch/batch/students-report",
        element: (
          <>
            <Outlet />
          </>
        ),
        children: [
          {
            path: "",
            element: (
              <LazyWrapper>
                <StudentsElpReportTable />
              </LazyWrapper>
            ),
          },
          {
            path: "details",
            children: [
              {
                path: "",
                element: (
                  <LazyWrapper>
                    <StudentElpTree pageTreetype="syllabus" />
                  </LazyWrapper>
                ),
              },
              {
                path: "question",
                element: (
                  <LazyWrapper>
                    <ElpReportQuestionDetails />
                  </LazyWrapper>
                ),
              },
            ],
          },
        ],
      },
      {
        path: "clients/branch/batch/students-report",
        element: (
          <>
            <Outlet />
          </>
        ),
        children: [
          {
            path: "",
            element: (
              <LazyWrapper>
                <StudentsElpReportTable />
              </LazyWrapper>
            ),
          },
          {
            path: "details",
            children: [
              {
                path: "",
                element: (
                  <LazyWrapper>
                    <StudentElpTree pageTreetype="syllabus" />
                  </LazyWrapper>
                ),
              },
              {
                path: "question",
                element: (
                  <LazyWrapper>
                    <ElpReportQuestionDetails />
                  </LazyWrapper>
                ),
              },
            ],
          },
        ],
      },
      {
        path: "branch/batch/students-report",
        element: (
          <>
            <Outlet />
          </>
        ),
        children: [
          {
            path: "",
            element: (
              <LazyWrapper>
                <StudentsElpReportTable />
              </LazyWrapper>
            ),
          },
          {
            path: "details",
            children: [
              {
                path: "",
                element: (
                  <LazyWrapper>
                    <StudentElpTree pageTreetype="syllabus" />
                  </LazyWrapper>
                ),
              },
              {
                path: "question",
                element: (
                  <LazyWrapper>
                    <ElpReportQuestionDetails />
                  </LazyWrapper>
                ),
              },
            ],
          },
        ],
      },
      {
        path: "sub-organization/batch/students-report",
        element: (
          <>
            <Outlet />
          </>
        ),
        children: [
          {
            path: "",
            element: (
              <LazyWrapper>
                <StudentsElpReportTable />
              </LazyWrapper>
            ),
          },
          {
            path: "details",
            children: [
              {
                path: "",
                element: (
                  <LazyWrapper>
                    <StudentElpTree pageTreetype="syllabus" />
                  </LazyWrapper>
                ),
              },
              {
                path: "question",
                element: (
                  <LazyWrapper>
                    <ElpReportQuestionDetails />
                  </LazyWrapper>
                ),
              },
            ],
          },
        ],
      },
    ],
  },
  {
    path: "/companies",
    element: (
      <LazyWrapper>
        <CompaniesListTable />
      </LazyWrapper>
    ),
  },
  {
    path: "/subjects",
    element: (
      <LazyWrapper>
        <SubjectSpecificLayout />
      </LazyWrapper>
    ),
    children: [
      {
        path: "details",
        element: <SubjectSpecificComponentWithRole />,
      },
      {
        path: "project-setup",
        element: (
          <LazyWrapper>
            <ProjectSetupPage />
          </LazyWrapper>
        ),
      },
      {
        path: "syllabuses",
        children: [
          {
            path: "",
            element: (
              <LazyWrapper>
                <SyllabusBaseComponent />
              </LazyWrapper>
            ),
          },
          {
            path: "syllabus",
            element: (
              <LazyWrapper>
                <SyllabusSpecificComponent />
              </LazyWrapper>
            ),
          },
          {
            path: "users",
            element: (
              <LazyWrapper>
                <SpecificUserComponent />
              </LazyWrapper>
            ),
          },
          {
            path: "students",
            element: (
              <LazyWrapper>
                <SpecificStudentComponent />
              </LazyWrapper>
            ),
          },
        ],
      },
      {
        path: "overview",
        element: (
          <LazyWrapper>
            <SubjectSpecificComponent />
          </LazyWrapper>
        ),
      },
      {
        path: "create-syllabus",
        element: (
          <LazyWrapper>
            <CreateSyllabus />
          </LazyWrapper>
        ),
      },
      {
        path: "edit-syllabus",
        element: (
          <LazyWrapper>
            <CreateSyllabus />
          </LazyWrapper>
        ),
      },
      {
        path: "users",
        element: (
          <LazyWrapper>
            <SpecificUserComponent />
          </LazyWrapper>
        ),
      },
      {
        path: "students",
        element: (
          <LazyWrapper>
            <SpecificStudentComponent />
          </LazyWrapper>
        ),
      },
      {
        path: "tutorials",
        element: (
          <LazyWrapper>
            <TutorialBaseComponent />
          </LazyWrapper>
        ),
      },
      {
        path: "playground",
        element: (
          <LazyWrapper>
            <PlaygroundBaseComponent />
          </LazyWrapper>
        ),
      },
      {
        path: "objectives",
        element: (
          <LazyWrapper>
            <ObjectivesBaseComponent />
          </LazyWrapper>
        ),
      },

      {
        path: "questions",
        element: (
          <LazyWrapper>
            <QuestionBaseComponent />
          </LazyWrapper>
        ),
      },
      {
        path: "database-management",
        element: (
          <LazyWrapper>
            <DatabaseManagementPage />
          </LazyWrapper>
        ),
      },
      {
        path: "test",
        element: (
          <LazyWrapper>
            <QuizBaseComponent />
          </LazyWrapper>
        ),
      },
      {
        path: "shared-question",
        element: (
          <LazyWrapper>
            <SharedQuestions />
          </LazyWrapper>
        ),
      },
      {
        path: "batches",
        element: (
          <>
            <Outlet />
          </>
        ),
        children: [
          {
            path: "",
            element: (
              <LazyWrapper>
                <BatchBaseComponent />
              </LazyWrapper>
            ),
          },
          {
            path: "batch-dashboard",
            children: [
              {
                path: "",
                element: (
                  <LazyWrapper>
                    <LandingPage />
                  </LazyWrapper>
                ),
              },
              {
                path: "details",
                children: [
                  {
                    path: "",
                    element: (
                      <LazyWrapper>
                        <StudentElpTree pageTreetype="syllabus" />
                      </LazyWrapper>
                    ),
                  },
                  {
                    path: "question",
                    element: (
                      <LazyWrapper>
                        <ElpReportQuestionDetails />
                      </LazyWrapper>
                    ),
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        path: "trainer-elp",
        element: (
          <LazyWrapper>
            <TrainerElpComponent />
          </LazyWrapper>
        ),
      },
      {
        path: "practice",
        element: (
          <LazyWrapper>
            <PracticeQuestionBaseComponent />
          </LazyWrapper>
        ),
      },
    ],
  },
  {
    path: "/batches",
    element: (
      <LazyWrapper>
        <StudentLayoutComponent />
      </LazyWrapper>
    ),
    children: [
      {
        path: "",
        element: (
          <LazyWrapper>
            <StudentBatchList />
          </LazyWrapper>
        ),
      },
    ],
  },
  {
    path: "/activities",
    children: [
      {
        path: "myQuiz",
        element: (
          <LazyWrapper>
            <MyQuizLayout />
          </LazyWrapper>
        ),
        children: [
          {
            path: "",
            element: (
              <LazyWrapper>
                <MyQuizList type="quiz" />
              </LazyWrapper>
            ),
          },
          {
            path: "instruction",
            element: (
              <LazyWrapper>
                <InstructionLayout type="quiz" />
              </LazyWrapper>
            ),
          },
          {
            path: "quiz-screen",
            element: (
              <LazyWrapper>
                <ExamLayoutScreen />
              </LazyWrapper>
            ),
          },
          {
            path: "result",
            element: (
              <LazyWrapper>
                <ExamLayoutScreen />
              </LazyWrapper>
            ),
          },
          {
            path: "details",
            element: (
              <LazyWrapper>
                <ResultDetails />
              </LazyWrapper>
            ),
          },
        ],
      },
      {
        path: "quizGroup",
        element: (
          <LazyWrapper>
            <QuizGroupLayout />
          </LazyWrapper>
        ),
        children: [
          {
            path: "",
            element: (
              <LazyWrapper>
                <StudentQuizGroupList />
              </LazyWrapper>
            ),
          },
          {
            path: "quiz",
            element: (
              <LazyWrapper>
                <RenderQuizGroup />
              </LazyWrapper>
            ),
          },
          {
            path: "instruction",
            element: (
              <LazyWrapper>
                <InstructionLayout type="quiz" />
              </LazyWrapper>
            ),
          },
          {
            path: "quiz-screen",
            element: (
              <LazyWrapper>
                <ExamLayoutScreen />
              </LazyWrapper>
            ),
          },
          {
            path: "result",
            element: (
              <LazyWrapper>
                <ExamLayoutScreen />
              </LazyWrapper>
            ),
          },
          {
            path: "details",
            element: (
              <LazyWrapper>
                <ResultDetails />
              </LazyWrapper>
            ),
          },
        ],
      },
      {
        path: "tournaments",
        element: (
          <LazyWrapper>
            <TournamentLayout />
          </LazyWrapper>
        ),
        children: [
          {
            path: "",
            element: (
              <LazyWrapper>
                <StudentTournamentsList type="tournament" />
              </LazyWrapper>
            ),
          },
          {
            path: "tournament-screen",
            element: (
              <LazyWrapper>
                <ExamLayoutScreen />
              </LazyWrapper>
            ),
          },
          {
            path: "tournamentResult",
            element: (
              <LazyWrapper>
                <TournamentLayout />
              </LazyWrapper>
            ),
            children: [
              {
                path: "",
                element: (
                  <LazyWrapper>
                    <ExamLayoutScreen />
                  </LazyWrapper>
                ),
              },
              {
                path: "questionDetails",
                element: (
                  <LazyWrapper>
                    <QuestionDetails />
                  </LazyWrapper>
                ),
              },
            ],
          },
          {
            path: "tournamentDetails",
            element: (
              <LazyWrapper>
                <TournamentLayout />
              </LazyWrapper>
            ),
            children: [
              {
                path: "",
                element: (
                  <LazyWrapper>
                    <CompletedTournamentDetailsList />
                  </LazyWrapper>
                ),
              },
              {
                path: "questionDetails",
                element: (
                  <LazyWrapper>
                    <QuestionDetails />
                  </LazyWrapper>
                ),
              },
            ],
          },
          {
            path: "tournament-instruction",
            element: (
              <LazyWrapper>
                <InstructionLayout type="tournament" />
              </LazyWrapper>
            ),
          },
        ],
      },
    ],
  },
  {
    path: "/dashboard",
    element: (
      <LazyWrapper>
        <DashboardBaseComponent />
      </LazyWrapper>
    ),
  },
  {
    path: "feedback",
    element: (
      <LazyWrapper>
        <StudentFeedbackPage />
      </LazyWrapper>
    ),
  },
  {
    path: "/report",
    element: <div>Report</div>,
  },
  {
    path: "/batches",
    children: [
      {
        path: "test",
        element: (
          <LazyWrapper>
            <StudentQuizLayout />
          </LazyWrapper>
        ),
        children: [
          {
            path: "",
            element: (
              <LazyWrapper>
                <ResourceQuizList type="assessment" />
              </LazyWrapper>
            ),
          },
          {
            path: "instruction",
            element: (
              <LazyWrapper>
                <InstructionLayout type="assessment" />
              </LazyWrapper>
            ),
          },
          {
            path: "test-screen",
            element: (
              <LazyWrapper>
                <ExamLayoutScreen />
              </LazyWrapper>
            ),
          },
          {
            path: "result",
            element: (
              <LazyWrapper>
                <ExamLayoutScreen />
              </LazyWrapper>
            ),
          },
          {
            path: "details",
            element: (
              <LazyWrapper>
                <ResultDetails />
              </LazyWrapper>
            ),
          },
        ],
      },
      {
        path: "elp",
        element: (
          <LazyWrapper>
            <StudentElp />
          </LazyWrapper>
        ),
      },
      {
        path: "workspace",
        element: (
          <LazyWrapper>
            <Workspace />
          </LazyWrapper>
        ),
      },
      {
        path: "practicequestion",
        element: (
          <LazyWrapper>
            <StudentAssignment />
          </LazyWrapper>
        ),
      },
    ],
  },
  {
    path: "/quiz",
    element: (
      <LazyWrapper>
        <QuizListTable />
      </LazyWrapper>
    ),
  },
  {
    path: "/quizGroup",
    element: (
      <LazyWrapper>
        <QuizGroupLayout />
      </LazyWrapper>
    ),
    children: [
      {
        path: "",
        element: (
          <LazyWrapper>
            <QuizGroupListTable />
          </LazyWrapper>
        ),
      },
      {
        path: "quizzesList",
        element: (
          <LazyWrapper>
            <QuizGroupQuizzesList />
          </LazyWrapper>
        ),
      },
    ],
  },
  {
    path: "/categories",
    element: (
      <LazyWrapper>
        <CategoriesLayout />
      </LazyWrapper>
    ),
    children: [
      {
        path: "",
        element: (
          <LazyWrapper>
            <CategoriesTable />
          </LazyWrapper>
        ),
      },
      {
        path: "questions",
        element: (
          <LazyWrapper>
            <CategoryQuestionTable />
          </LazyWrapper>
        ),
      },
      {
        path: "topics",
        element: (
          <LazyWrapper>
            <CategoryTopicsTable />
          </LazyWrapper>
        ),
      },
      {
        path: "lists",
        element: (
          <LazyWrapper>
            <CategoryListTable />
          </LazyWrapper>
        ),
      },
    ],
  },

  {
    path: "/tournaments",
    element: (
      <Suspense fallback="Loading...">
        <TournamentsListTable />
      </Suspense>
    ),
  },
  {
    path: "/template",
    element: (
      <LazyWrapper>
        <TemplateLayout />
      </LazyWrapper>
    ),
    children: [
      {
        path: "",
        element: (
          <LazyWrapper>
            <CategoryTemplateList />
          </LazyWrapper>
        ),
      },
      {
        path: "create-template",
        element: (
          <LazyWrapper>
            <OrgRegistrationForm />
          </LazyWrapper>
        ),
      },
    ],
  },
  {
    path: "/traits",
    element: (
      <LazyWrapper>
        <TraitsLayout />
      </LazyWrapper>
    ),
    children: [
      {
        path: "",
        element: (
          <LazyWrapper>
            <TraitsList />
          </LazyWrapper>
        ),
      },
      {
        path: "create-template",
        element: (
          <LazyWrapper>
            <OrgRegistrationForm />
          </LazyWrapper>
        ),
      },
    ],
  },
  {
    path: "/paragraphs",
    element: (
      <LazyWrapper>
        <ParagraphsLayout />
      </LazyWrapper>
    ),
    children: [
      {
        path: "",
        element: (
          <LazyWrapper>
            <ParagraphsList />
          </LazyWrapper>
        ),
      },
    ],
  },
  {
    path: "/admin-dashboard",
    element: (
      <LazyWrapper>
        <DashboardLayout />
      </LazyWrapper>
    ),
    children: [
      {
        path: "",
        element: (
          <LazyWrapper>
            <OrganizationListTable />
          </LazyWrapper>
        ),
      },
      {
        path: "quiz",
        element: (
          <LazyWrapper>
            <QuizListTable />
          </LazyWrapper>
        ),
      },
      {
        path: "tournaments",
        element: (
          <Suspense fallback="Loading...">
            <TournamentsListTable />
          </Suspense>
        ),
      },
      {
        path: "batches",
        element: (
          <>
            <Outlet />
          </>
        ),
        children: [
          {
            path: "",
            element: (
              <LazyWrapper>
                <SubjectBaseComponent />
              </LazyWrapper>
            ),
          },
          {
            path: "trainer-batches",
            element: (
              <LazyWrapper>
                <BatchBaseComponent />
              </LazyWrapper>
            ),
          },
          {
            path: "students-report",
            element: (
              <>
                <Outlet />
              </>
            ),
            children: [
              {
                path: "",
                element: (
                  <LazyWrapper>
                    <StudentsElpReportTable />
                  </LazyWrapper>
                ),
              },
              {
                path: "details",
                children: [
                  {
                    path: "",
                    element: (
                      <LazyWrapper>
                        <StudentElpTree pageTreetype="syllabus" />
                      </LazyWrapper>
                    ),
                  },
                  {
                    path: "question",
                    element: (
                      <LazyWrapper>
                        <ElpReportQuestionDetails />
                      </LazyWrapper>
                    ),
                  },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
  {
    path: "/wordzapp",
    element: (
      <LazyWrapper>
        <WordzappLayout />
      </LazyWrapper>
    ),
    children: [
      {
        path: "",
        element: (
          <LazyWrapper>
            <WordzappHome />
          </LazyWrapper>
        ),
      },
      {
        path: "lists",
        element: (
          <LazyWrapper>
            <WordzappListsView />
          </LazyWrapper>
        ),
      },
      {
        path: "lists/groups",
        element: (
          <LazyWrapper>
            <WordzappListsView />
          </LazyWrapper>
        ),
      },
      {
        path: "lists/groups/words",
        element: (
          <LazyWrapper>
            <WordzappListsView />
          </LazyWrapper>
        ),
      },
    ],
  },
];
